import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  onSnapshot,
  orderBy,
  limit,
  serverTimestamp,
  addDoc,
  deleteDoc
} from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { OperationType, FirestoreErrorInfo, ChatMessage } from '../types';
import { isAdmin, ADMIN_EMAILS } from '../constants';

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export const dbService = {
  async getUser(uid: string) {
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      return snap.exists() ? snap.data() : null;
    } catch (e) {
      handleFirestoreError(e, OperationType.GET, `users/${uid}`);
    }
  },

  async getUserByEmail(email: string) {
    try {
      const q = query(collection(db, 'users'), where('email', '==', email));
      const snap = await getDocs(q);
      if (!snap.empty) {
        return { uid: snap.docs[0].id, ...snap.docs[0].data() } as any;
      }
      return null;
    } catch (e) {
      handleFirestoreError(e, OperationType.LIST, 'users');
      return null;
    }
  },

  async checkEmailExists(email: string) {
    try {
      // Check in users
      const qUser = query(collection(db, 'users'), where('email', '==', email));
      const userSnap = await getDocs(qUser);
      if (!userSnap.empty) {
        return { exists: true, message: 'Email sudah terdaftar sebagai pengguna.' };
      }

      // Check in registrations
      const qReg = query(collection(db, 'registrations'), where('email', '==', email));
      const regSnap = await getDocs(qReg);
      if (!regSnap.empty) {
         return { exists: true, message: 'Email ini sudah memiliki pendaftaran Kapster pending.' };
      }

      return { exists: false };
    } catch (e) {
      handleFirestoreError(e, OperationType.LIST, 'users');
      return { exists: false };
    }
  },

  async createUser(profile: any) {
    try {
      await setDoc(doc(db, 'users', profile.uid), profile);
    } catch (e) {
      handleFirestoreError(e, OperationType.CREATE, `users/${profile.uid}`);
    }
  },

  async getBarbers() {
    try {
      const q = query(
        collection(db, 'users'), 
        where('role', '==', 'barber'),
        where('isVerified', '==', true)
      );
      const snap = await getDocs(q);
      return snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter((b: any) => b.id !== 'barber_seed_1' && b.id !== 'barber_seed_2');
    } catch (e) {
      handleFirestoreError(e, OperationType.LIST, 'users');
    }
  },

  async getServices() {
    try {
      const snap = await getDocs(collection(db, 'services'));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      handleFirestoreError(e, OperationType.LIST, 'services');
    }
  },

  async createBooking(booking: any) {
    try {
      const data = {
        ...booking,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, 'bookings'), data);
    } catch (e) {
      handleFirestoreError(e, OperationType.CREATE, 'bookings');
    }
  },

  async updateBookingStatus(bookingId: string, status: string) {
    try {
      await updateDoc(doc(db, 'bookings', bookingId), { status });
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `bookings/${bookingId}`);
    }
  },

  async updateBookingPayment(bookingId: string, paymentMethod: string, paymentProofUrl: string) {
    try {
      await updateDoc(doc(db, 'bookings', bookingId), { 
        paymentMethod,
        paymentProofUrl, 
        paymentStatus: 'Menunggu Verifikasi' 
      });
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `bookings/${bookingId}`);
    }
  },

  async verifyPayment(bookingId: string, paymentStatus: 'Belum Bayar' | 'Menunggu Verifikasi' | 'Lunas') {
    try {
      await updateDoc(doc(db, 'bookings', bookingId), { paymentStatus });
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `bookings/${bookingId}`);
    }
  },

  async updateBookingLocation(bookingId: string, location: string) {
    try {
      await updateDoc(doc(db, 'bookings', bookingId), { location });
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `bookings/${bookingId}`);
    }
  },
  
  async rateBooking(bookingId: string, barberId: string, rating: number, review: string) {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      const barberRef = doc(db, 'users', barberId);

      // 1. Update Booking
      await updateDoc(bookingRef, { rating, review });

      // 2. Update Barber Average Rating
      // Note: In production, this should be done via Cloud Functions to ensure accuracy and security.
      const barberSnap = await getDoc(barberRef);
      if (barberSnap.exists()) {
        const barberData = barberSnap.data();
        const currentRating = barberData.rating || 0;
        const currentCount = barberData.ratingCount || 0;
        
        const newCount = currentCount + 1;
        const newRating = ((currentRating * currentCount) + rating) / newCount;

        await updateDoc(barberRef, {
          rating: Number(newRating.toFixed(1)),
          ratingCount: newCount
        });
      }
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `rate-booking/${bookingId}`);
    }
  },

  onBarbersChange(callback: (data: any[]) => void) {
    const q = query(
      collection(db, 'users'), 
      where('role', '==', 'barber'),
      where('isVerified', '==', true)
    );
    return onSnapshot(q, (snap) => {
      const barbers = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter((b: any) => b.id !== 'barber_seed_1' && b.id !== 'barber_seed_2');
      callback(barbers);
    }, (e) => handleFirestoreError(e, OperationType.LIST, 'users'));
  },

  onServicesChange(callback: (data: any[]) => void) {
    return onSnapshot(collection(db, 'services'), (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (e) => handleFirestoreError(e, OperationType.LIST, 'services'));
  },

  onBarberBookings(barberId: string, callback: (data: any[]) => void) {
    const q = query(
      collection(db, 'bookings'), 
      where('barberId', '==', barberId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (e) => handleFirestoreError(e, OperationType.LIST, 'bookings'));
  },

  onCustomerBookings(customerId: string, callback: (data: any[]) => void) {
    const q = query(
      collection(db, 'bookings'), 
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (e) => handleFirestoreError(e, OperationType.LIST, 'bookings'));
  },

  onBookingsChange(callback: (data: any[]) => void) {
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (e) => handleFirestoreError(e, OperationType.LIST, 'bookings'));
  },

  onUsersChange(callback: (data: any[]) => void) {
    return onSnapshot(collection(db, 'users'), (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (e) => handleFirestoreError(e, OperationType.LIST, 'users'));
  },

  onRegistrationsChange(callback: (data: any[]) => void) {
    return onSnapshot(collection(db, 'registrations'), (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (e) => handleFirestoreError(e, OperationType.LIST, 'registrations'));
  },

  async submitBarberRegistration(data: any) {
    try {
      await addDoc(collection(db, 'registrations'), data);
    } catch (e) {
      handleFirestoreError(e, OperationType.CREATE, 'registrations');
    }
  },

  async updateUserRole(uid: string, role: string) {
    try {
      await updateDoc(doc(db, 'users', uid), { role });
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `users/${uid}`);
    }
  },

  async toggleUserVerification(uid: string, isVerified: boolean) {
    try {
      await updateDoc(doc(db, 'users', uid), { isVerified });
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `users/${uid}`);
    }
  },

  async updateUser(uid: string, data: any) {
    try {
      await updateDoc(doc(db, 'users', uid), data);
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `users/${uid}`);
    }
  },

  async deleteUser(uid: string) {
    try {
      await deleteDoc(doc(db, 'users', uid));
    } catch (e) {
      handleFirestoreError(e, OperationType.DELETE, `users/${uid}`);
    }
  },

  async deleteRegistration(id: string) {
    try {
      await deleteDoc(doc(db, 'registrations', id));
    } catch (e) {
      handleFirestoreError(e, OperationType.DELETE, `registrations/${id}`);
    }
  },

  // Portfolio Operations
  async addPortfolioItem(userId: string, item: any) {
    try {
      const colRef = collection(db, 'users', userId, 'portfolio');
      await setDoc(doc(colRef, item.id), item);
    } catch (e) {
      handleFirestoreError(e, OperationType.CREATE, `users/${userId}/portfolio`);
    }
  },

  async removePortfolioItem(userId: string, itemId: string) {
    try {
      await deleteDoc(doc(db, 'users', userId, 'portfolio', itemId));
    } catch (e) {
      handleFirestoreError(e, OperationType.DELETE, `users/${userId}/portfolio/${itemId}`);
    }
  },

  onPortfolioChange(userId: string, callback: (items: any[]) => void) {
    const colRef = collection(db, 'users', userId, 'portfolio');
    const q = query(colRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (e) => handleFirestoreError(e, OperationType.LIST, `users/${userId}/portfolio`));
  },

  async getPortfolio(userId: string) {
    try {
      const q = query(collection(db, 'users', userId, 'portfolio'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      handleFirestoreError(e, OperationType.LIST, `users/${userId}/portfolio`);
    }
  },

  async updateBarberStatus(uid: string, status: string) {
    try {
      await updateDoc(doc(db, 'users', uid), { status });
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `users/${uid}`);
    }
  },

  async deleteBooking(bookingId: string) {
    try {
      await deleteDoc(doc(db, 'bookings', bookingId));
    } catch (e) {
      handleFirestoreError(e, OperationType.DELETE, `bookings/${bookingId}`);
    }
  },

  async clearNonAdminUsers() {
    try {
      const snap = await getDocs(collection(db, 'users'));
      const deletePromises = snap.docs
        .filter(d => !isAdmin(d.data().email))
        .map(d => deleteDoc(doc(db, 'users', d.id)));
      await Promise.all(deletePromises);
    } catch (e) {
      handleFirestoreError(e, OperationType.DELETE, 'users');
    }
  },

  // Chat Operations
  async sendMessage(bookingId: string, message: Omit<ChatMessage, 'id' | 'timestamp' | 'bookingId'>) {
    try {
      const msgRef = collection(db, 'bookings', bookingId, 'messages');
      await addDoc(msgRef, {
        ...message,
        bookingId,
        timestamp: serverTimestamp()
      });
    } catch (e) {
      handleFirestoreError(e, OperationType.WRITE, `bookings/${bookingId}/messages`);
    }
  },

  onMessagesChange(bookingId: string, currentUser: any, callback: (messages: ChatMessage[]) => void) {
    const filters = [];
    if (!isAdmin(currentUser?.email)) { // Not Admin
      if (currentUser?.role === 'customer') {
        filters.push(where('customerId', '==', currentUser.uid));
      } else if (currentUser?.role === 'barber') {
        filters.push(where('barberId', '==', currentUser.uid));
      }
    }

    const q = query(
      collection(db, 'bookings', bookingId, 'messages'),
      ...filters,
      orderBy('timestamp', 'asc')
    );
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() }) as ChatMessage));
    }, (e) => handleFirestoreError(e, OperationType.LIST, `bookings/${bookingId}/messages`));
  },

  async markMessagesAsRead(bookingId: string, messageIds: string[]) {
    try {
      const promises = messageIds.map(id => 
        updateDoc(doc(db, 'bookings', bookingId, 'messages', id), { isRead: true })
      );
      await Promise.all(promises);
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `bookings/${bookingId}/messages`);
    }
  }
};
